// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

// Angular Material Modules
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

// Third party
import * as _ from 'lodash';
import moment from 'moment';

// Local library
import { AuthFacade, ILogin } from '@amwager/authentication/data-access';
import {
  ConvertToRangePipe,
  CountdownPipe,
  GetCurrentRacePipe,
  GetEventStatusPipe,
  GetRaceStatusPipe,
  GetTrackTypePipe,
  HorseWeightPipe,
  InactivityService,
  LegHeightDirective,
  MatNotificationService,
  NumberWithCommasPipe,
  OrdinalsPipe,
  RaceStringToRangePipe,
  SanitizerPipe,
  SortEventsPipe,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';
import {
  IDataPrograms,
  IEventTrackRaceState,
  ISelectedRace,
  IWagerDeepLink,
  ProgramService,
  WagerDeepLinkService,
  WagerFacade,
  WagerSelectionService,
  WagerService
} from '@amwager/wager/data-access';
import {
  convertTimeToMinutes,
  GUEST,
  IAccountPreferencesModel,
  IGetEventsDetailsResponse,
  IPool,
  IRace,
  isOnline$,
  IToteResultResponse,
  LoggerService,
  SERVER_ERROR_MESSAGE
} from '@amwager/util-ts';
import {
  CommonFacade,
  ENVIRONMENT,
  PermissionService,
  SessionService
} from '@amwager/data-access';

// Local file
// import { PlayVideoComponent } from '../play-video/play-video.component';
import { RaceComponent } from '../race/race.component';
import { RunnerComponent } from '../runner/runner.component';
import { PoolComponent } from '../pool/pool.component';
import { ModifierComponent } from '../modifier/modifier.component';
import { BetsAmountComponent } from '../bets-amount/bets-amount.component';
import { BetPreviewComponent } from '../bet-preview/bet-preview.component';
import { BetsComponent } from '../bets/bets.component';
import { Dictionary, EntityState } from '@ngrx/entity';
// import { EventTrackListComponent } from '../event-track-list/event-track-list.component';
import {
  Observable,
  Subscription,
  distinctUntilChanged,
  interval,
  take,
  takeUntil,
  timeInterval
} from 'rxjs';
import { HorseLoaderService, VideoComponent } from '@amwager/feature';
import { AccountFacade, ProfileService } from '@amwager/account/data-access';
import { ProfileComponent } from '@amwager/account/feature-mat';
import { MatrixComponent } from '../matrix/matrix.component';
import { TicketFacade } from '@amwager/ticket/data-access';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LiveVideoComponent } from '../live-video/live-video.component';
import { ReplayVideoComponent } from '../replay-video/replay-video.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'feature-mat-wager',
  templateUrl: './wager.component.html',
  styleUrls: ['./wager.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RaceStringToRangePipe,
    GetCurrentRacePipe,
    GetRaceStatusPipe,
    SortEventsPipe,
    GetEventStatusPipe,
    GetTrackTypePipe,
    ConvertToRangePipe,
    LegHeightDirective,
    OrdinalsPipe,
    HorseWeightPipe,
    RaceComponent,
    PoolComponent,
    NumberWithCommasPipe,
    ModifierComponent,
    RunnerComponent,
    BetsAmountComponent,
    BetPreviewComponent,
    BetsComponent,
    CountdownPipe,
    VideoComponent,
    SanitizerPipe,
    MatrixComponent,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    ProfileComponent,
    TranslatePipe
  ],
  providers: [
    DatePipe,
    GetEventStatusPipe,
    SortEventsPipe
  ]
})
export class WagerComponent extends
UnsubscribeObservableService implements OnInit, OnDestroy  {
  @ViewChild('eventSelection') eventSelection?: any;
  @ViewChild('favorites') favorites!: ElementRef;

  SERVER_ERROR_MESSAGE = SERVER_ERROR_MESSAGE;

  userFullName$: string | undefined;
  userEmail!: string;

  playLiveVideo: boolean = false;
  // triggerLiveVideo: boolean = false;
  currentRaceStatus!: number;

  // Drives the "more track info" panel above the pools
  showMoreTrackInfo: boolean = false;

  // Start as an empty array so template `events?.length` is safe on first render
  events: IGetEventsDetailsResponse[] | any[] = [];
  currentEventId!: any;
  selectedEvent!: IGetEventsDetailsResponse;
  prevSelectedEvent?: IGetEventsDetailsResponse;
  selectedModifier!: string;
  dblPkArray!: any;
  sortBy: string = 'MTP';
  eventTypeFilter: string[] = [
    'Thoroughbred',
    'Quarterhorse',
    'Greyhound',
    'Harness',
    'International'
  ];
  triggerFromPreference: boolean = false;
  triggerFromBetResult: boolean = false;
  triggerFromVideoReplay: boolean = false;
  triggerAutoUpdateEvents: boolean = false;
  latestFinishedEvent!: IGetEventsDetailsResponse;
  latestRaceId!: number;
  emptyEvent!: IGetEventsDetailsResponse;

  isEventTrackRaceRequestingAPI: boolean = true;

  /**
   * Flips true on the first non-empty events emission. Gates
   * the skeleton/content/empty three-way switch so refreshes
   * never flicker back to the skeleton.
   */
  private hasEverLoadedEvents = false;

  /**
   * Flips true on the first matrix payload with a real response
   * (WIN or TRU). Sticky — keeps refreshes from flipping the
   * skeleton back on. Paired with `hasMinDisplayTimeElapsed` so a
   * stale cache hit on nav-back doesn't hide the skeleton early.
   */
  private hasEverLoadedRaceMatrix = false;

  /** One-shot guard so My Bets is fetched only once per page load/refresh. */
  private hasLoadedInitialBetSlip = false;

  /**
   * Flips true a short delay after mount. Enforces a minimum
   * skeleton time so fast/cached emissions don't cause a flash,
   * and so child components (race chips → matrix API) have room
   * to clear + refetch fresh data before content is revealed.
   */
  private hasMinDisplayTimeElapsed = false;

  /** Timer handle for {@link hasMinDisplayTimeElapsed}; cleared on destroy. */
  private minDisplayTimeoutId?: ReturnType<typeof setTimeout>;

  /**
   * Pre-selection captured from the results page's "click Open
   * race" hand-off. Consumed once during the initial events pick
   * so we land on that exact event/race instead of falling back
   * to `pickLatestOpenEvent`.
   */
  private pendingInitialSelection?: {
    eventId?: string;
    runId?: string;
    raceId?: number;
  };

  /**
   * Shareable wager link (from the Help panel) parsed off the URL
   * hash. Event/race are selected via {@link pendingInitialSelection};
   * the pool/modifier/amount/runners are replayed once the grid renders.
   */
  private pendingDeepLink?: IWagerDeepLink;

  /** One-shot guard so a deep-link is replayed once per page load. */
  private hasAppliedDeepLink = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadingModal: any;

  setListFavorites!: Set<string>;
  listOfFavoriteEvents!: any;

  /**
   * Flips true on the first favorites emission (even empty).
   * Used by {@link showFavoritesSkeleton} — kept separate from
   * `listOfFavoriteEvents` because that field is transiently
   * reassigned during derivation.
   */
  private hasEverLoadedFavorites = false;

  isSystemError: boolean = false;
  showHorseLoader: boolean = false;

  private firstOnline = true;

  private modal: any;
  private triggerMatrixModal_subs?: Subscription;
  private getIsConnectedInternetSubs?: Subscription;

  private triggerMatrixModal?: string;

  private showAlertInfoHandler?: HTMLIonAlertElement;
  private showAlertErrorHandler?: HTMLIonAlertElement;

  selectedRace?: ISelectedRace;
  // Race being viewed inside the runner table for DBL/PK pools; drives the
  // race-details header. Falls back to selectedRace when unset.
  displayedRace?: ISelectedRace;
  selectedPool!: IPool;
  selectedAmount!: number;
  showBetPayload: any;

  isOnline: boolean = true;

  // True when inline profile panel is open — hides main wager content
  isProfileOpen: boolean = false;

  /** Tote event ids with a program on sale today — gates the PRGM link */
  private programEventIds = new Set<string>();

  /** Program store slice, kept for the refresh guard */
  private programsState?: IDataPrograms;

  // resultData!: { events: any; selectedEvent: any }
  resultData!: Observable<{ events: any; selectedEvent: any }>;

  readonly dialog = inject(MatDialog);

  // Guards against duplicate live-video dialogs. `playLivePending`
  // gates the store observer so only user-initiated plays open a
  // dialog (stale state on remount is ignored). `liveVideoDialogRef`
  // tracks the current instance so a second click is a no-op.
  private playLivePending: boolean = false;
  private liveVideoDialogRef?: MatDialogRef<LiveVideoComponent>;

  // Same guard pattern for the runner replay dialog. `replayPending`
  // gates the store observer to user-initiated replays; `replayDialogRef`
  // stops a second dialog stacking on top of an open one.
  private replayPending: boolean = false;
  private replayDialogRef?: MatDialogRef<ReplayVideoComponent>;

  /**
   * True during the initial mount (fresh load OR nav-back) until
   * ALL of these are satisfied:
   *   1. Events emission has arrived
   *   2. Race matrix has emitted a real response for the current race
   *   3. Minimum display time has elapsed (prevents flash)
   * Background refreshes never re-enable it (all three flags are
   * sticky). Short-circuits on `isSystemError` to show the empty
   * / error card instead.
   * @returns {boolean} true = show skeleton, false = show content/empty
   */
  get showInitialSkeleton(): boolean {
    if (this.isSystemError) return false;

    return (
      !this.hasEverLoadedEvents
      // || !this.hasEverLoadedRaceMatrix
      || !this.hasMinDisplayTimeElapsed
    );
  }

  /**
   * True while the favorites row should paint skeleton chips
   * (first paint only). Not tied to background refreshes so
   * loaded chips never flicker back to skeleton.
   * @returns {boolean} true = show skeleton, false = show chips
   */
  get showFavoritesSkeleton(): boolean {
    return (
      !this.hasEverLoadedFavorites
      || this.listOfFavoriteEvents === undefined
    );
  }

  constructor(
    private readonly authFacade: AuthFacade,
    private readonly accountFacade: AccountFacade,
    private readonly cdRef: ChangeDetectorRef,
    private readonly wagerFacade: WagerFacade,
    private readonly commonFacade: CommonFacade,
    private readonly ticketFacade: TicketFacade,
    private readonly elementRef: ElementRef,
    private readonly permissionService: PermissionService,
    private readonly sessionService: SessionService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly matNotificationService: MatNotificationService,
    private readonly getEventStatusPipe: GetEventStatusPipe,
    private readonly sortEventsPipe: SortEventsPipe,
    private readonly renderer: Renderer2,
    private readonly inactivityService: InactivityService,
    private readonly wagerService: WagerService,
    private readonly wagerSelectionService: WagerSelectionService,
    private readonly wagerDeepLinkService: WagerDeepLinkService,
    private readonly loggerService: LoggerService,
    private readonly horseLoaderService: HorseLoaderService,
    private readonly profileService: ProfileService,
    private readonly programService: ProgramService,
    @Inject(ENVIRONMENT) readonly environment: string,
  ) {
    super();

    // Route state pushed by results' Open race click. Only
    // available during the navigation itself, so grab it here.
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras?.state as
      { eventId?: string; runId?: string; raceId?: number } | undefined;
    if (state?.eventId || state?.raceId) {
      this.pendingInitialSelection = {
        eventId: state?.eventId,
        runId: state?.runId,
        raceId: state?.raceId,
      };
    }

    // Shareable wager link (Help panel "Generate & Copy Encoded Runners"):
    // read the hash captured at startup (a bootstrap redirect can strip the
    // router fragment first) and seed the event/race hand-off so the existing
    // selection flow lands on the linked event/race; pool/modifier/amount/
    // runners are replayed in ngOnInit once the grid renders (mirrors the
    // legacy web router which read window.location.hash directly).
    // OLD: read from the router fragment, which a redirect could strip first.
    // const deepLink = this.wagerDeepLinkService.parseFromHash(
    //   this.route.snapshot.fragment ?? ''
    // );
    const deepLink = this.wagerDeepLinkService.isConsumed
      ? null
      : this.wagerDeepLinkService.peekInitialDeepLink();
    if (deepLink) {
      this.pendingDeepLink = deepLink;
      this.pendingInitialSelection = {
        eventId: deepLink.eventId,
        raceId: deepLink.raceId ? Number(deepLink.raceId) : undefined,
      };
    }

    this.profileService.showProfile$
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((show: boolean) => {
        this.isProfileOpen = show;
        this.cdRef.markForCheck();
      });

      this.authFacade.isLoginSuccessResponse$().subscribe(async(response?: any) => {
        if (response === undefined)
          return;

        // this.showHorseLoader = true;
        // this.horseLoaderService.show();
      });

    this.authFacade.isLogoutSuccess$().subscribe((isLogout?: boolean) => {
      if (isLogout) {
        (this.eventSelection as any)['value'] = null; // Reset value (optional)
        (document.querySelector('.select-popover') as any)?.dismiss(); // Close the alert overlay if open

        this.matNotificationService.hide();
        this.showAlertInfoHandler?.dismiss();
        this.showAlertErrorHandler?.dismiss();

        this.cdRef.markForCheck(); // re-render for template
      }
    });

    this.authFacade.login$().subscribe((loginData?: ILogin) => {
      this.isSystemError = !this.sessionService.hasToken() ? true : false;
    });

    // support if no token from cookies after 3 seconds of initialization
    setTimeout(() => {
      this.authFacade.login$()
        .pipe(take(1))
        .subscribe((loginData?: ILogin) => {
          if (!this.sessionService.hasToken()) this.authFacade.loginGuest();
        });
    }, 3000);

    this.getIsConnectedInternetSubs = this.commonFacade.getIsConnectedInternet$()
      .subscribe((isConnectedInternet) => {

        if (isConnectedInternet?.isConnected) {
          // this.loggerService.log('You are online');

          if (this.firstOnline) {
            this.firstOnline = false;
            return;
          }

          setTimeout(() => {
            this.authFacade.login$()
              .pipe(take(1))
              .subscribe((loginData?: ILogin) => {
                if (!this.sessionService.hasToken()) this.authFacade.loginGuest();
                else {
                  // init states
                  this.wagerFacade.getSelectEventTrackRaceState$()
                    .pipe(take(1))
                    .subscribe((eventTrackRaceState: IEventTrackRaceState) => {
                      if (_.isEmpty(eventTrackRaceState)) {
                        // init state managements
                        this.commonFacade.initState('AppComponent');
                        this.authFacade.initState('AppComponent');
                        this.wagerFacade.initState('AppComponent');
                        this.ticketFacade.initState('AppComponent');
                        this.accountFacade.initState('AppComponent');

                        this.wagerFacade.getSelectAllEventTrackRace$()
                          .pipe(take(1))
                          .subscribe((allEventTrackRace: IGetEventsDetailsResponse[]) => {
                            if (_.isEmpty(allEventTrackRace)) this.wagerFacade.getEventsDetails("WagerComponent:constructor");
                          });
                      }
                      else {
                        this.wagerFacade.getSelectAllEventTrackRace$()
                          .pipe(take(1))
                          .subscribe((allEventTrackRace: IGetEventsDetailsResponse[]) => {
                            if (_.isEmpty(allEventTrackRace)) this.wagerFacade.getEventsDetails("WagerComponent:constructor");
                            else {
                              if (
                                this.selectedRace?.RaceId
                                && this.selectedRace?.runId
                                && this.selectedRace?.eventId
                              ) {
                                this.wagerFacade.getRace(
                                  'WagerComponent:constructor',
                                  this.selectedRace?.RaceId,
                                  this.selectedRace?.runId,
                                  this.selectedRace?.eventId
                                );
                              }
                            }
                          });
                      }
                    });

                }
              });
          }, 500);
        }
      });

    // update events every 25000
    interval(25000)
      .pipe(
        distinctUntilChanged() // Suppress consecutive duplicates
      )
      .subscribe((value) => {
        // this.loggerService.log('interval:', value);

        this.isSystemError = !this.sessionService.hasToken() ? true : false;

        this.wagerFacade.toUpdateGeneralEvents('WagerComponent:constructor');

        // don't exit if video is playing
        if (document.querySelector("iframe.live-video") || document.querySelector("video")) {
          this.inactivityService.resetInactivityTimer('WagerComponent:constructor');
        }

        if (!this.isOnline) return;

        if (!this.sessionService.hasToken()) this.authFacade.loginGuest();
        else {
          // init states
          this.wagerFacade.getSelectEventTrackRaceState$()
            .pipe(take(1))
            .subscribe((eventTrackRaceState: IEventTrackRaceState) => {
              if (_.isEmpty(eventTrackRaceState)) {
                // init state managements
                this.commonFacade.initState('AppComponent');
                this.authFacade.initState('AppComponent');
                this.wagerFacade.initState('AppComponent');
                this.ticketFacade.initState('AppComponent');
                this.accountFacade.initState('AppComponent');

                this.wagerFacade.getSelectAllEventTrackRace$()
                  .pipe(take(1))
                  .subscribe((allEventTrackRace: IGetEventsDetailsResponse[]) => {
                    if (_.isEmpty(allEventTrackRace)) this.wagerFacade.getEventsDetails("WagerComponent:constructor");
                  });
              }
              else {
                // get event details if no data from state store
                this.wagerFacade.getSelectAllEventTrackRace$()
                  .pipe(take(1))
                  .subscribe((allEventTrackRace: IGetEventsDetailsResponse[]) => {
                    if (_.isEmpty(allEventTrackRace)) this.wagerFacade.getEventsDetails("WagerComponent:constructor");
                  });
              }
            });
        }
      });

    // auto select current race if coming from login page
    this.commonFacade.getBackNavigation$()
      .pipe(
        distinctUntilChanged() // Suppress consecutive duplicates
      )
      .subscribe(async (source) => {
        if (source.includes('login')) {

          if (_.isEmpty(this.events)) return;

          this.prevSelectedEvent = undefined;

          let isFinalAllEvent: boolean[] = [];
          _.range(this.events.length).forEach((count) => {
            isFinalAllEvent.push(
              ["Finished", "Closed"].includes(this.events[count]?.ToteResponse?.EventDetail?.EventStatus)
                ? true
                : false
            )
          });

          if (isFinalAllEvent.every(value => value === true)) {

            this.matNotificationService.showAlert(
              'Info',
              'All events are in Final status. Please login or signup to see more content of AmWager.',
              '',
              'Login',
              '',
              'login'
            );
            return;
          }

          if (["Finished", "Closed"].includes(this.selectedEvent?.ToteResponse?.EventDetail?.EventStatus)) {
            this.selectedEvent = this.events[0];
            this.currentEventId = this.selectedEvent?.id;
            this.wagerFacade.selectedEventTrack('WagerComponent:constructor', this.selectedEvent);
          }

          this.wagerFacade.selectedRace(
            'WagerComponent:constructor',
            {
              ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
              runId: this.selectedEvent?.ToteResponse?.RunId,
              eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
            }
          );

          this.cdRef.markForCheck();
        }
      });

    if (this.triggerMatrixModal_subs) {
      this.triggerMatrixModal_subs?.unsubscribe();
      this.triggerMatrixModal_subs = undefined;
    }

    // this.triggerMatrixModal_subs = this.wagerFacade.triggerMatrixModal$()
    //   .pipe(
    //     distinctUntilChanged() // Suppress consecutive duplicates
    //   )
    //   .subscribe(async (trigger) => {

    //     if (this.triggerMatrixModal === trigger) {
    //       this.triggerMatrixModal_subs?.unsubscribe();
    //       this.triggerMatrixModal_subs = undefined;
    //       return;
    //     }

    //     this.triggerMatrixModal = trigger;

    //     if (!trigger) return;

    //     if (this.modal) {
    //       this.modal?.dismiss();
    //       this.modal = undefined;
    //       return;
    //     }

    //     (document.querySelector('.show-modal') as any)?.dismiss(); // Close the modal overlay if open

    //     this.modal = await this.modalCtrl.create({
    //       component: MatrixComponent,
    //       cssClass: 'matrix',
    //       backdropDismiss: false
    //     });
    //     this.modal.present();

    //     this.modal.onDidDismiss().then(() => {
    //       this.modal = undefined;
    //     });
    //   });

    this.wagerFacade.toUpdateGeneralEvents$()
      .pipe(
        distinctUntilChanged() // Suppress consecutive duplicates
      )
      .subscribe((toUpdate) => {
        if (!toUpdate) return;

        // re-render data list
        this.wagerFacade.getSelectAllEventTrackRace$()
          .pipe(take(1))
          .subscribe(async(allEventTrackRace: IGetEventsDetailsResponse[]) => {

            if (_.isEmpty(allEventTrackRace)) return;

            this.triggerAutoUpdateEvents = false;
            this.events = allEventTrackRace as any;
            const result = await this.wagerService.onSelectSortEvents(
              this.sortBy,
              this.eventTypeFilter,
              this.prevSelectedEvent,
              this.triggerFromPreference,
              this.triggerFromBetResult,
              this.triggerFromVideoReplay,
              this.latestFinishedEvent,
              this.emptyEvent,
              this.events
            );

            result.subscribe((callback: any) => {
              this.events = callback?.events;
              this.selectedEvent = callback?.selectedEvent;
              this.currentEventId = this.selectedEvent?.id;
              this.triggerFromPreference = callback?.triggerFromPreference;
              this.triggerFromBetResult = callback?.triggerFromBetResult;
              this.triggerFromVideoReplay = callback?.triggerFromVideoReplay;
              this.cdRef.detectChanges(); // re-render for template
            });

            this.cdRef.detectChanges(); // re-render for template
          });
      });


    this.wagerFacade.getSelectAllEventTrackRace$()
      .subscribe(async(allEventTrackRace: IGetEventsDetailsResponse[]) => {

        this.triggerAutoUpdateEvents = true;

        // Flip the skeleton gate off once real data lands.
        if (!_.isEmpty(allEventTrackRace)) {
          this.hasEverLoadedEvents = true;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.events = allEventTrackRace as any;

        // Apply a favorites/results hand-off up-front (before the async
        // sort) so #eventSelection shows the chosen track immediately.
        // Sets prevSelectedEvent so the sort keeps it (no flicker).
        this.applyPendingSelection(this.events);

        const result = await this.wagerService.onSelectSortEvents(
          this.sortBy,
          this.eventTypeFilter,
          this.prevSelectedEvent,
          this.triggerFromPreference,
          this.triggerFromBetResult,
          this.triggerFromVideoReplay,
          this.latestFinishedEvent,
          this.emptyEvent,
          this.events
        );

        result?.subscribe((callback: any) => {
          if (callback?.events !== undefined && callback?.selectedEvent !== undefined) {
            this.events = callback?.events;
            this.selectedEvent = callback?.selectedEvent;
            this.currentEventId = this.selectedEvent?.id;
            this.triggerFromPreference = callback?.triggerFromPreference;
            this.triggerFromBetResult = callback?.triggerFromBetResult;
            this.triggerFromVideoReplay = callback?.triggerFromVideoReplay;

            // First landing (no prior user pick):
            //  1. If results handed off a specific event/race,
            //     honor it — consumed once.
            //  2. Otherwise, skip Finished/Closed events so wager
            //     opens on the latest open race.
            if (this.prevSelectedEvent === undefined) {
              const pending = this.pendingInitialSelection;
              const preSelected = pending?.eventId
                ? _.find(
                    this.events,
                    (event) => event?.id === pending?.eventId
                  )
                : undefined;

              if (preSelected) {
                const preRace = pending?.raceId
                  ? _.find(
                      preSelected?.ToteResponse?.EventDetail?.Races,
                      (race: IRace) => race?.RaceId === pending?.raceId
                    )
                  : undefined;

                this.selectedEvent = preSelected;
                this.currentEventId = preSelected?.id;
                // Persist the pick so later Pusher-driven event refreshes
                // (e.g. a race going final) don't re-run first-landing
                // selection and switch the user's chosen event track.
                this.prevSelectedEvent = preSelected;

                this.wagerFacade.selectedEventTrack(
                  'WagerComponent:constructor:pending', preSelected
                );
                this.wagerFacade.selectedRace(
                  'WagerComponent:constructor:pending',
                  {
                    ...(preRace
                      || preSelected?.ToteResponse?.EventDetail?.currentRace) as any,
                    runId: preSelected?.ToteResponse?.RunId,
                    eventId: preSelected?.ToteResponse?.EventDetail.EventId
                  }
                );

                this.pendingInitialSelection = undefined;
              } else {
                // No open events left — nothing wagerable, so
                // hand off to results and exit this callback.
                if (
                  this.events.length > 0
                  && !this.findFirstOpenEvent(this.events)
                ) {
                  this.router.navigate(['/results']);
                  return;
                }

                const latest = this.pickLatestOpenEvent(this.events);
                if (latest && latest?.id !== this.selectedEvent?.id) {
                  this.selectedEvent = latest;
                  this.currentEventId = latest?.id;

                  this.wagerFacade.selectedEventTrack(
                    'WagerComponent:constructor:latest', latest
                  );
                  this.wagerFacade.selectedRace(
                    'WagerComponent:constructor:latest',
                    {
                      ...latest?.ToteResponse?.EventDetail?.currentRace as any,
                      runId: latest?.ToteResponse?.RunId,
                      eventId: latest?.ToteResponse?.EventDetail.EventId
                    }
                  );
                }

                // Persist the auto-pick (even when it already matched
                // events[0]) so subsequent Pusher event refreshes keep
                // this track selected instead of re-picking latest open.
                this.prevSelectedEvent = this.selectedEvent;
              }
            }

            if (!this.selectedEvent) {
              // Helper returns undefined only for empty lists, which
              // we've already excluded via the outer `!== undefined`
              // check — safe to fall back to `events[0]`.
              this.selectedEvent =
                this.pickLatestOpenEvent(this.events) || this.events[0];
              this.prevSelectedEvent = this.selectedEvent;
              this.currentEventId = this.selectedEvent?.id;
              this.wagerFacade.selectedEventTrack('WagerComponent:constructor', this.selectedEvent);

              this.wagerFacade.selectedRace(
                'WagerComponent:constructor',
                {
                  ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
                  runId: this.selectedEvent?.ToteResponse?.RunId,
                  eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
                }
              );

            }

            // Fetch My Bets once on page load/refresh so saved/active/
            // closed tickets survive a browser reload. Guarded to logged-in,
            // non-guest users to avoid a 401 and the prior 10x re-fetch.
            this.loadInitialBetSlip(this.selectedEvent?.ToteResponse?.RunId);

            // OLD (buggy): re-filtered using listOfFavoriteEvents (now event
            // objects), so `.includes(event.id)` was always false → cleared favorites.
            // this.listOfFavoriteEvents = _.filter(
            //   this.events, (event) => {
            //     return this.listOfFavoriteEvents?.includes(event.id)
            //   }
            // );
            // NEW: rebuild from the favorites Set so events refresh keeps chips.
            this.rebuildFavoriteEvents();
          }
        });

        this.cdRef.markForCheck(); // re-render for template

      });

    this.wagerFacade.isEventTrackRaceRequestingAPI$()
      .subscribe((isRequestingAPI: boolean) => {
        // Mirror facade flag into local state for the template gate
        const wasLoading = !!this.isEventTrackRaceRequestingAPI;
        this.isEventTrackRaceRequestingAPI = isRequestingAPI;

        // Toggle horse loader only on real state transitions
        if (isRequestingAPI && !wasLoading && !this.showHorseLoader) {
          // this.horseLoaderService.show();
          this.showHorseLoader = true;
        } else if (!isRequestingAPI && wasLoading && this.showHorseLoader) {
          // this.horseLoaderService.hide();
        }

        this.cdRef.markForCheck(); // re-render for template
      });

    // Flip the matrix-ready gate as soon as WIN/TRU odds arrive.
    // Sticky — later clears (race change) don't reopen the skeleton
    // because the min-display timer only runs once per mount.
    this.wagerFacade.getRaceAllOddsMatrixes$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((matrix) => {
        if (matrix?.WIN?.response || matrix?.TRU?.response) {
          if (!this.hasEverLoadedRaceMatrix) {
            this.hasEverLoadedRaceMatrix = true;
            this.cdRef.markForCheck();
          }
        }
      });

    this.wagerFacade.getSelectEventTrackRaceEntities$()
      .subscribe((allEventTrackRace: Dictionary<IGetEventsDetailsResponse>) => {
        if (_.isEmpty(allEventTrackRace)) return;

        this.selectedEvent = allEventTrackRace[this.selectedEvent?.id as any] as IGetEventsDetailsResponse;
        this.currentEventId = this.selectedEvent?.id;

        // this.showAlertError(this.selectedEvent?.Error);

        this.cdRef.markForCheck();

      });


    this.wagerFacade.getEventLiveVideo$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback === undefined || callback?.response === undefined) return;

        // Only open for user-initiated plays; ignore stale state
        // emitted on remount and short-circuit if one is already up.
        if (!this.playLivePending) return;
        if (this.liveVideoDialogRef) return;

        this.playLivePending = false;

        this.liveVideoDialogRef = this.dialog.open(LiveVideoComponent, {
          data: callback?.response.Link,
          width: '500px',
          height: '300px',
          hasBackdrop: false,
          disableClose: false,
          panelClass: 'live-video-dialog'
        });

        this.liveVideoDialogRef.afterClosed().subscribe(() => {
          this.liveVideoDialogRef = undefined;
        });
      });

    // A video/replay API error (`from: true`) doesn't resolve the success
    // stream, so release the in-flight guards here — otherwise the buttons
    // stay stuck after the error message is dismissed.
    this.commonFacade.isErrorAPI$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((error: any) => {
        if (error?.from !== true) return;
        this.playLivePending = false;
        this.replayPending = false;
      });

    this.wagerFacade.getLatestFinishedEvent$()
      .subscribe((callback: any) => {
        if (callback?.event === undefined) return;

        this.wagerFacade.toUpdateGeneralEvents('WagerComponent: clickEventList');
        if (callback.raceId === undefined) {
          this.triggerFromBetResult = true;
          this.latestFinishedEvent = callback.event;
        }
        else if (callback.event !== undefined && callback.raceId !== undefined)
        {
          this.triggerFromVideoReplay = true;
          this.latestFinishedEvent = callback.event;
          this.latestRaceId = callback.raceId;
        }

        // this.cdRef.markForCheck();
      });

    this.accountFacade.isGetAccountPreferencesSuccess$()
      .subscribe(async(callback) => {

        if (callback?.isSuccess === undefined) return;

        const response: IAccountPreferencesModel = callback?.response?.AccountPreferences;
        this.sortBy = response?.SortBy || 'MTP';
        this.eventTypeFilter = response?.EventTypeFilter || [];

        this.wagerFacade.toUpdateGeneralEvents('WagerComponent: clickEventList');

        this.triggerFromPreference = true;

        this.cdRef.markForCheck();
      });

    // QA testing
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.commonFacade.qaTesting$().subscribe((qaTesting: any) => {
      if (!qaTesting) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.events = qaTesting?.wagerCustom as any;

      this.selectedEvent = this.events[0];
      this.prevSelectedEvent = this.events[0];
      this.currentEventId = this.selectedEvent?.id;
      this.wagerFacade.selectedEventTrack('WagerComponent:constructor', this.selectedEvent);

      this.cdRef.detectChanges(); // re-render for template
    });

    this.wagerFacade.getSelectedRace$().subscribe((selectedRace) => {
      this.selectedRace = selectedRace;
      // New base race -> drop any DBL/PK column override in the header.
      this.displayedRace = undefined;
      this.publishCurrentSelection();
      this.cdRef.markForCheck();
    });

    // set to default event from tap races - footer
    this.wagerFacade.getDefaultEvent$()
      .subscribe((isSetDefault) => {

        if (isSetDefault && this.events.length > 0) {
          this.wagerFacade.setDefaultEvent(false);

          const newEvents = this.sortEventsPipe.transform(
            this.events,
            this.sortBy,
            this.eventTypeFilter,
            this.setListFavorites
          );

          const firstEvent = _.find(newEvents, event =>
            _.get(event, 'ToteResponse.EventDetail.EventStatus') === 'Open' &&
            _.get(event, 'ToteResponse.EventDetail.currentRace.RaceStatus') === 0
          );

          this.selectEvent({target: {value: firstEvent}});
        }

      });

    this.wagerService.observerPreferenceFavorites()
      .subscribe((listFavorites?: string[]) => {
      // OLD (buggy): stored ids in listOfFavoriteEvents then reused that same
      // field for the event-object filter, which desynced later refreshes.
      // this.listOfFavoriteEvents = listFavorites;
      // this.listOfFavoriteEvents = _.filter(
      //   this.events, (event) => {
      //     return this.listOfFavoriteEvents?.includes(event.id)
      //   }
      // );
      // NEW: keep favorite ids in the Set and derive chips via shared helper.
      this.setListFavorites = new Set(listFavorites);
      this.rebuildFavoriteEvents();

      // First emission = favorites loaded (even if empty).
      this.hasEverLoadedFavorites = true;
      this.cdRef.markForCheck();
    });

    this.wagerFacade.getRaceResult$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((results?: IToteResultResponse) => {
        const replayVideo: string | undefined = results?.replayVideo;

        // Only open for user-initiated replays; the URL persists in the
        // store and re-emits on every result refresh, so without this
        // guard the dialog would reopen/stack.
        if (!replayVideo) {
          // The replay API returns HTTP 200 with an `Error` body (no failed
          // action fires), so an empty/errored replay would otherwise leave
          // the guard stuck — release it here so the button works again.
          if (this.replayPending && (results as any)?.errorMsg) this.replayPending = false;
          return;
        }
        if (!this.replayPending) return;
        if (this.replayDialogRef) {
          this.replayPending = false;
          return;
        }

        this.replayPending = false;

        // min-height keeps the title, video and runners table
        // visible; it still grows if the content is taller.
        this.replayDialogRef = this.dialog.open(ReplayVideoComponent, {
          data: replayVideo,
          width: '500px',
          minHeight: '600px',
          hasBackdrop: false,
          disableClose: false,
          panelClass: 'replay-video-dialog'
        });

        this.replayDialogRef.afterClosed().subscribe(() => {
          this.replayDialogRef = undefined;
        });
      });

  }

  /**
   * Arm the replay observer when a runner replay is requested so a
   * single dialog opens once the URL lands in the store
   * @returns {void}
   */
  onReplayRequested(): void {
    if (this.replayDialogRef || this.replayPending) return;
    this.replayPending = true;
  }

  ngOnInit(): void {
    // update branch
    this.wagerService.checkConnectivity(
      isOnline$, this.environment
    ).subscribe((online: any) => {
      this.isOnline = online;
      this.commonFacade.setIsConnectedInternet('WagerComponent:checkConnectivity', online);
    });

    this.authFacade.getUserFullName$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe( res => {

        // user logged out
        if (res === undefined) {
          this.sortBy = 'MTP';
          this.eventTypeFilter = [
            'Thoroughbred',
            'Quarterhorse',
            'Greyhound',
            'Harness',
            'International'
          ];
        }

        this.userFullName$ = res;
        this.cdRef.markForCheck(); // re-render for template
      });

    this.getIsConnectedInternetSubs?.unsubscribe();

    this.userEmail = this.sessionService.getUserEmail();
    this.cdRef.markForCheck(); // re-render for template

    // Min-skeleton-time (~500 ms). Ensures fast/cached emissions
    // don't cause a flash on nav-back and gives child components
    // time to clear + refetch fresh matrix / bet data.
    this.minDisplayTimeoutId = setTimeout(() => {
      this.hasMinDisplayTimeElapsed = true;
      this.cdRef.markForCheck();
    }, 500);

    // Replay a shareable wager link, if the URL carried one. Mark it
    // consumed so a later WagerComponent instance won't re-apply it.
    if (this.pendingDeepLink) {
      this.wagerDeepLinkService.markConsumed();
      this.applyDeepLinkSelections(this.pendingDeepLink);
    }

    this.loadProgramTracks();
  }

  /**
   * Cleanup on destroy — clears the min-display timer so it
   * cannot fire after the component is torn down (nav-away).
   * @returns {void}
   */
  override ngOnDestroy(): void {
    if (this.minDisplayTimeoutId) {
      clearTimeout(this.minDisplayTimeoutId);
      this.minDisplayTimeoutId = undefined;
    }

    // Close any lingering live-video dialog so it can't outlive
    // this page and overlap with the replay dialog on results
    if (this.liveVideoDialogRef) {
      this.liveVideoDialogRef.close();
      this.liveVideoDialogRef = undefined;
    }
    this.playLivePending = false;

    // Same teardown for the replay dialog so a late store emission
    // can't reopen it after the page is gone.
    if (this.replayDialogRef) {
      this.replayDialogRef.close();
      this.replayDialogRef = undefined;
    }
    this.replayPending = false;

    super.ngOnDestroy();
  }

  /**
   * Observers
   * Set list of favorite events
   * @returns {void}
   */
  observerPreferenceFavorites(): void {
    this.accountFacade.getPreferenceFavorites$()
      .subscribe((listFavorites?: string[]) => {
        this.setListFavorites = new Set(listFavorites);
      });
  }

  /**
   * Rebuild favorites chips from the intersection of the favorites
   * Set and the current events list. Mirrors the results page so
   * chips survive event refreshes.
   * @returns {void}
   */
  private rebuildFavoriteEvents(): void {
    if (!this.setListFavorites || this.setListFavorites.size === 0) {
      this.listOfFavoriteEvents = [];
      return;
    }

    this.listOfFavoriteEvents = _.filter(
      this.events,
      (event) => this.setListFavorites.has(event?.id as string)
    );
  }

  /**
   * Fetch saved/active/closed My Bets once per page load/refresh.
   * Skips guests/anonymous users (no real tickets, would 401) and
   * runs a single time via the one-shot guard.
   * @param {string} runId Run id of the selected event
   * @returns {void}
   */
  private loadInitialBetSlip(runId?: string): void {
    if (this.hasLoadedInitialBetSlip || !runId) return;

    const isLoggedInNonGuest =
      this.sessionService.hasToken() &&
      this.sessionService.getUserEmail() !== GUEST &&
      this.sessionService.getRoleId() !== 10;

    if (!isLoggedInNonGuest) return;

    this.hasLoadedInitialBetSlip = true;
    this.wagerService.getBetSlip(runId);
  }

  /**
   * Check obj selected event
   * @param {any} selectedEvent current event
   * @returns {void}
   */
  isSelectedEvent(selectedEvent: any): void {
    this.compareWith(selectedEvent);
  }

  /**
   * Compare event obj to current obj
   * @param {any} o1 current obj
   * @param {any} o2 new obj
   * @returns {any} final obj
   */
  compareWith(o1?: any, o2?: any): any {
    return o1 && o2 ? o1.id === o2.id : o1 === o2;
  }

  /**
   * Check if live video is allowed
   * @returns {boolean} true/false
   */
  isLiveVideoAllowed(): boolean {
    return this.wagerService.isLiveVideoAllowed(
      this.selectedRace,
      this.selectedEvent
    );
  }

  /**
   * Play race video
   * @returns {void}
   */
  async playVideo(): Promise<void> {
    if (!this.isLiveVideoAllowed()) return;
    if (this.selectedRace?.RaceStatus === 3) return;

    // Bail out if a live video is already up or an open is in flight
    if (this.liveVideoDialogRef || this.playLivePending) return;

    if (!this.isOnline) {
      this.commonFacade.setIsConnectedInternet('WagerComponent:playVideo', false);
    }

    if (!this.hasPermission()) {

      if (this.sessionService.getUserEmail() === GUEST || !this.sessionService.hasToken()) {
        this.router.navigate(['login']);
      }
      else {
        this.showAlertInfo('You are not an Identified User.');
      }

      return;
    }

    if (this.selectedEvent) {
      if (this.currentRaceStatus !== 3) {
        this.playLivePending = true;

        this.wagerFacade.getEventRaceVideoLive(
          'WagerComponent:playVideo',
          {
            toteEventId: this.selectedEvent?.ToteResponse?.EventDetail?.EventId,
            width: 720,
            height: 576
          },
          this.selectedEvent?.VideoType === 'IGB' ? 'IGB' : 'default'
        );
      }
    }
  }

  /**
   * Set new status in race
   * @param {number} $status current race status
   * @returns {void}
   */
  doSetRaceStatus($status: number): void {
    this.currentRaceStatus = $status;
  }

  /**
   * Close current race video
   * @param {any} $event current event video
   * @returns {void}
   */
  closeVideo($event: any): void {
    this.playLiveVideo = !this.playLiveVideo;
  }

  // async openEventTrackList(): Promise<void> {
  //   if (!this.hasPermission()) return;

  //   const modal = await this.modalCtrl.create({
  //     component: EventTrackListComponent,
  //     cssClass: 'event-track-list'
  //   });
  //   modal.present();

  //   const { role } = await modal.onWillDismiss();
  //   if (role) this.playLiveVideo = false;

  //   this.cdRef.detectChanges();
  // }

  /**
   * Triggered if user Click event even though without choosing any event
   * @returns {void}
   */
  clickEventList(): void {

    // Close inline profile panel if open (user is wagering again)
    if (this.profileService.isProfileOpen) {
      this.profileService.closeProfile();
    }

    // UI: favorite divider selection option
    if (
      this.sessionService.getUserEmail() !== 'Guest' &&
      this.sessionService.getRoleId() !== 10 &&
      this.setListFavorites !== undefined
    ) {
      setTimeout(() => {
        const eventItemAll = document
          .querySelector('.select-event-track')
          ?.querySelectorAll('.event-item');

        const newEvents = this.sortEventsPipe.transform(
          this.events,
          this.sortBy,
          this.eventTypeFilter,
          this.setListFavorites
        );

        this.renderer.setAttribute(
          eventItemAll?.[_.filter(newEvents, ['isFavorite', true])?.length-1],
          'style',
          'border-bottom-width: medium !important;' +
          'border-bottom-style: dashed !important;'
        );

      }, 300);
    }

    this.wagerFacade.toUpdateGeneralEvents('WagerComponent: clickEventList');

    // re-render data list
    this.wagerFacade.getSelectAllEventTrackRace$()
      .pipe(take(1))
      .subscribe(async(allEventTrackRace: IGetEventsDetailsResponse[]) => {
        this.events = allEventTrackRace;
        const result = await this.wagerService.onSelectSortEvents(
          this.sortBy,
          this.eventTypeFilter,
          this.prevSelectedEvent,
          this.triggerFromPreference,
          this.triggerFromBetResult,
          this.triggerFromVideoReplay,
          this.latestFinishedEvent,
          this.emptyEvent,
          this.events
        );

        result.subscribe((callback: any) => {
          this.events = callback?.events;
          this.selectedEvent = callback?.selectedEvent;
          this.currentEventId = this.selectedEvent?.id;
          this.triggerFromPreference = callback?.triggerFromPreference;
          this.triggerFromBetResult = callback?.triggerFromBetResult;
          this.triggerFromVideoReplay = callback?.triggerFromVideoReplay;
        });

        this.cdRef.detectChanges();
      });
  }

  /**
   * Set event
   * @param {any} event current event to be set
   * @returns {void}
   */
  selectEvent(event?: any): void {

    // Close profile panel — clicking event/favorite returns to wager view
    if (this.profileService.isProfileOpen) {
      this.profileService.closeProfile();
    }

    this.triggerAutoUpdateEvents = true;
    this.selectedEvent = _.find(this.events, (event) => event.id === this.currentEventId) || [];
    this.prevSelectedEvent = _.find(this.events, (event) => event.id === this.currentEventId);

    // clear active legs/round
    this.wagerService.clearLegRoundSelector(this.elementRef);

    if (event !== undefined) {
      this.selectedEvent = event;
      this.prevSelectedEvent = event;
      this.currentEventId = this.selectedEvent?.id;
    }
    // this.selectedEvent = ev.target.value;

    // Finished event/race isn't wagerable — send the user to results for
    // the selected track + race. Anonymous users (no session: not logged
    // in and not a guest) must log in first.
    const eventDetail = this.selectedEvent?.ToteResponse?.EventDetail;
    const isFinished =
      eventDetail?.currentRace?.RaceStatus === 3 // Final
      || eventDetail?.EventStatus === 'Finished'
      || eventDetail?.EventStatus === 'Final';

    if (isFinished) {
      if (!this.sessionService.hasToken() || this.sessionService.getUserEmail() === GUEST) {
        this.router.navigate(['/login']);
        return;
      }

      this.router.navigate(['/results'], {
        state: {
          eventId: this.selectedEvent?.id,
          runId: this.selectedEvent?.ToteResponse?.RunId,
          raceId: eventDetail?.currentRace?.RaceId,
        }
      });
      return;
    }

    this.wagerFacade.selectedEventTrack('WagerComponent:constructor', this.selectedEvent);
    this.selectedModifier = '';

    // Wipe matrix slots up-front so a stale event's odds/totals/
    // will-pays/win-chart never leak into the new selection even
    // if the new race payload is missing RaceId.
    this.clearMatrixStoreSlots('WagerComponent:selectEvent');

    // const currentRace = _.find(
    //   this.selectedEvent?.ToteResponse?.EventDetail?.Races, race => _.has(race, 'Current')
    // );

    if (this.selectedEvent?.ToteResponse?.EventDetail?.EventStatus === 'Finished') {
      this.wagerFacade.selectedRace(
        'WagerComponent:selectEvent',
        {
          // ...this.selectedEvent?.ToteResponse?.EventDetail?.Races[0] as any,
          // ...currentRace as any,
          ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
          runId: this.selectedEvent?.ToteResponse?.RunId,
          eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
        }
      );
    }
    else {
      this.wagerFacade.selectedRace(
        'WagerComponent:selectEvent',
        {
          ...this.selectedEvent?.ToteResponse?.EventDetail?.currentRace as any,
          // ...currentRace as any,
          runId: this.selectedEvent?.ToteResponse?.RunId,
          eventId: this.selectedEvent?.ToteResponse?.EventDetail.EventId
        }
      );
    }

    this.cdRef.markForCheck(); // re-render for template
  }

  /**
   * Clear all four matrix-related store slots in one shot so the
   * child matrix component drops stale odds / totals / will-pays /
   * win-chart data on event switch.
   * @param {string} sourceInitiator Debug tag for the dispatches
   * @returns {void}
   */
  private clearMatrixStoreSlots(sourceInitiator: string): void {
    this.wagerFacade.clearSelectedRaceMatrix(sourceInitiator);
    this.wagerFacade.clearSelectedRacePoolsTotals(sourceInitiator);
    this.wagerFacade.clearSelectedRaceWillPays(sourceInitiator);
    this.wagerFacade.clearRaceAllOddsMatrixes(sourceInitiator);
  }

  /**
   * Open weather
   * @returns {void}
   */
  openWeather(): void {
    this.matNotificationService.showAlert(
      'Info',
      "We're working hard to bring this feature to you! Stay tuned for updates.",
      '',
      '',
      'Close'
    );
  }

  /**
   * Toggle the "more track info" panel shown above the pools
   * @returns {void}
   */
  openInformation(): void {
    this.showMoreTrackInfo = !this.showMoreTrackInfo;
    this.cdRef.markForCheck();
  }

  /**
   * Open notes
   * @returns {void}
   */
  openNotes(): void {
    this.matNotificationService.showAlert(
      'Info',
      "We're working hard to bring this feature to you! Stay tuned for updates.",
      '',
      '',
      'Close'
    );
  }

  /**
   * Get pool
   * @param {any} data current pool and dblPkArray
   * @returns {void}
   */
  doGetPool(data?: any): void {
    this.selectedRace = data?.selectedRace;
    this.selectedPool = data?.pool;
    this.dblPkArray = data?.dblPkArray;
    // New pool -> reset the header back to the pool's base race.
    this.displayedRace = undefined;
    this.publishCurrentSelection();
  }

  /**
   * Race-details header source: the race being viewed in the runner
   * table (DBL/PK), falling back to the selected race.
   * @returns {ISelectedRace | undefined} Race to render in the header
   */
  get raceDetails(): ISelectedRace | undefined {
    return this.displayedRace ?? this.selectedRace;
  }

  /**
   * Follows the R1/R2/R3 column shown in the runner table so the
   * race-details header reflects the race currently being viewed.
   * @param {any} race Race object emitted by the runner component
   * @returns {void}
   */
  onDisplayedRaceChange(race?: any): void {
    this.displayedRace = race;
    this.cdRef.markForCheck();
  }

  /**
   * Get modifier
   * @param {any} modifier current modifier
   * @returns {void}
   */
  doGetModifier(modifier?: any): void {
    this.selectedModifier = modifier;
    this.publishCurrentSelection();
  }

  /**
   * Get bet amount
   * @param {any} amount current amount
   * @returns {void}
   */
  doGetBetAmount(amount?: any): void {
    this.selectedAmount = amount;
    this.publishCurrentSelection();
  }

  /**
   * Show bet
   * @param {any} payload current payload
   * @returns {void}
   */
  doShowBet(payload?: any): void {
    this.showBetPayload = payload;
    this.publishCurrentSelection();
  }

  /**
   * Mirror the current wager context (event/race/pool/amount/modifier +
   * any selected runners) into the shared selection service so the Help
   * panel can build an encoded-runners link — no bet required, matching
   * the legacy web behavior.
   * @returns {void}
   */
  private publishCurrentSelection(): void {
    const eventId = this.selectedEvent?.ToteResponse?.EventDetail?.EventId;

    // OLD: required at least one selected runner before publishing, so
    // the link couldn't be generated without building a bet.
    // const legs = (payload?.legs ?? [])
    //   .filter((leg) => (leg?.Runners ?? '').trim().length > 0)
    //   .map((leg) => ({ Runners: leg.Runners ?? '' }));
    // if (!eventId || !this.selectedPool?.PoolId || !legs.length) {
    //   this.wagerSelectionService.clearSelection();
    //   return;
    // }

    // Event + pool context is enough; runners are optional.
    if (!eventId || !this.selectedPool?.PoolId) {
      this.wagerSelectionService.clearSelection();
      return;
    }

    const legs = (this.showBetPayload?.legs ?? [])
      .filter((leg: { Runners?: string }) => (leg?.Runners ?? '').trim().length > 0)
      .map((leg: { Runners?: string }) => ({ Runners: leg.Runners ?? '' }));

    this.wagerSelectionService.setSelection({
      eventId,
      runId: this.selectedEvent?.ToteResponse?.RunId,
      raceId: this.selectedRace?.RaceId ?? '',
      poolId: this.selectedPool.PoolId,
      baseAmount: this.selectedAmount ?? '',
      modifier: this.selectedModifier ?? '',
      legs
    });
  }

  /**
   * Check if user role is allowed
   * @returns {boolean} true/false
   */
  hasPermission(): boolean {
    if (this.permissionService.isAllowed()) {
      return true;
    }

    this.router.navigate(['/login']);
    return false;
  }

  /**
   * Track item by identities
   * Use the unique ID property for tracking
   * @param {number} index current index is
   * @param {any} item current data is
   * @returns {void}
   */
  trackByFn(index: number, item: any): number {
    return item.id;
  }

  /**
   * First event that still has a wagerable race (not Finished /
   * Cancelled / Closed). Returns undefined when every event is done.
   * @param {IGetEventsDetailsResponse[]} events sorted events list
   * @returns {IGetEventsDetailsResponse | undefined} first open event
   */
  private findFirstOpenEvent(
    events: IGetEventsDetailsResponse[]
  ): IGetEventsDetailsResponse | undefined {
    return _.find(events, (event: IGetEventsDetailsResponse) => {
      const raceStatus = event?.ToteResponse?.EventDetail?.currentRace?.RaceStatus;
      const eventStatus = event?.ToteResponse?.EventDetail?.EventStatus;
      return (
        raceStatus !== 3
        && raceStatus !== 4
        && eventStatus !== 'Finished'
        && eventStatus !== 'Closed'
      );
    });
  }

  /**
   * Latest open event, or `events[0]` when everything is done.
   * @param {IGetEventsDetailsResponse[]} events sorted events list
   * @returns {IGetEventsDetailsResponse | undefined} latest open event
   */
  private pickLatestOpenEvent(
    events: IGetEventsDetailsResponse[]
  ): IGetEventsDetailsResponse | undefined {
    if (!events?.length) return undefined;

    return this.findFirstOpenEvent(events) || events[0];
  }

  /**
   * Consume a favorites/results hand-off synchronously so the selected
   * track paints at once. Sets prevSelectedEvent so the downstream sort
   * keeps it. No-op once consumed or if the event isn't in the list yet
   * (a later emission retries).
   * @param {IGetEventsDetailsResponse[]} events current events list
   * @returns {void}
   */
  private applyPendingSelection(
    events: IGetEventsDetailsResponse[]
  ): void {
    const pending = this.pendingInitialSelection;
    if (!pending?.eventId || _.isEmpty(events)) return;

    const preSelected = _.find(events, (event) => event?.id === pending?.eventId);
    // TEMP diagnostics — remove once the deep-link flow is verified.
    if (this.pendingDeepLink) {
      console.warn(
        '[deep-link] applyPendingSelection want eventId =', pending?.eventId,
        '| found =', !!preSelected,
        '| events =', events.length,
        '| available ids =', events.map((e) => e?.id)
      );
    }
    if (!preSelected) return;

    const preRace = pending?.raceId
      ? _.find(
          preSelected?.ToteResponse?.EventDetail?.Races,
          (race: IRace) => race?.RaceId === pending?.raceId
        )
      : undefined;

    this.selectedEvent = preSelected;
    this.currentEventId = preSelected?.id;
    this.prevSelectedEvent = preSelected;

    this.wagerFacade.selectedEventTrack(
      'WagerComponent:applyPendingSelection', preSelected
    );
    this.wagerFacade.selectedRace(
      'WagerComponent:applyPendingSelection',
      {
        ...(preRace
          || preSelected?.ToteResponse?.EventDetail?.currentRace) as any,
        runId: preSelected?.ToteResponse?.RunId,
        eventId: preSelected?.ToteResponse?.EventDetail?.EventId
      }
    );

    // Consumed — main callback's first-landing block is now skipped.
    this.pendingInitialSelection = undefined;
    this.cdRef.markForCheck();
  }

  /**
   * show info message
   * @param {string} message current msg
   * @returns {void}
   */
  async showAlertInfo(message: string): Promise<void> {
    this.matNotificationService.showAlert('Info', message, '', '', 'Close');
  }

  /**
   * show error message
   * @param {string} error current msg
   * @returns {void}
   */
  async showAlertError(error: string): Promise<void> {
    this.matNotificationService.showAlert('Error', error, '', '', 'Close');
  }

  /**
   * reload whole page
   * @returns {void}
   */
  reloadPage(): void {
    window.location.reload();
  }

  /**
   * Retry events fetch from empty/error state without full page reload
   * @returns {void}
   */
  reloadEvents(): void {
    this.isSystemError = false;
    this.isEventTrackRaceRequestingAPI = true;
    this.hasEverLoadedEvents = false;
    this.wagerFacade.getEventsDetails('WagerComponent:reloadEvents');
    this.cdRef.markForCheck();
  }

  /**
   * Replay a shareable wager link by driving the same controls a user
   * would click: after the event/race land (via pendingInitialSelection),
   * apply the pool, modifier, amount, then the encoded runners. Mirrors
   * the legacy web router's `loadRace` / `clickRunners`.
   * @param {IWagerDeepLink} link Parsed wager deep-link
   * @returns {Promise<void>}
   */
  private async applyDeepLinkSelections(link: IWagerDeepLink): Promise<void> {
    if (this.hasAppliedDeepLink) return;
    this.hasAppliedDeepLink = true;

    // Shareable links are for verified users only (role 7). Everyone else
    // (guest or real) is dropped onto the plain /wager page — strip the hash
    // so nothing is replayed and the URL is clean.
    if (this.sessionService.getRoleId() !== 7) {
      this.location.replaceState(this.location.path(false));
      return;
    }

    // TEMP diagnostics — remove once the deep-link flow is verified.
    console.warn('[deep-link] applyDeepLinkSelections start', link);

    // Wait for the linked event to load, then select it ourselves — the
    // first-landing logic may pick a default before it arrives, so we don't
    // rely on that race.
    // OLD: only waited for the hand-off to select it; lost the race and
    // stayed on the default event.
    // if (link.eventId) {
    //   const eventReady = await this.waitForCondition(() => this.selectedEvent?.id === link.eventId);
    //   if (!eventReady) return;
    // }
    if (link.eventId) {
      const eventInList = await this.waitForCondition(
        () => !!_.find(this.events, (e) => e?.id === link.eventId)
      );
      // TEMP diagnostics — remove once the deep-link flow is verified.
      console.warn('[deep-link] eventInList =', eventInList, '| selectedEvent.id =', this.selectedEvent?.id);
      if (!eventInList) return;

      if (this.selectedEvent?.id !== link.eventId) {
        this.selectDeepLinkEvent(link);
      }

      const eventReady = await this.waitForCondition(
        () => this.selectedEvent?.id === link.eventId
      );
      // TEMP diagnostics — remove once the deep-link flow is verified.
      console.warn('[deep-link] eventReady =', eventReady, '| selectedEvent.id =', this.selectedEvent?.id);
      if (!eventReady) return;
    }

    // Finished event/race isn't wagerable — hand off to results with the
    // linked event/race auto-populated, and stop replaying wager controls.
    if (this.redirectToResultsIfFinished()) return;

    // Wait until the linked race is the active one so we don't click a
    // pool on the auto-selected default race.
    if (link.raceId) {
      const ready = await this.waitForCondition(
        () => String(this.selectedRace?.RaceId) === String(link.raceId)
      );
      if (!ready) return;
    }

    // Pool → resets modifier/amount, so it must run first. Click-and-confirm
    // (with retry) because an event auto-update can re-render and drop a
    // single click — the cause of multi-race pools (DBL/PK/GSL) not sticking.
    // OLD: single click, dropped when the grid re-rendered.
    // const poolEl = await this.waitForElement(`.pool-chip[data-pool-id="${link.poolId}"]`);
    // poolEl?.click();
    if (link.poolId) {
      const ok = await this.clickAndConfirm(
        `.pool-chip[data-pool-id="${link.poolId}"]`, 'active-pool'
      );
      // TEMP diagnostics — remove once the deep-link flow is verified.
      console.warn('[deep-link] pool', link.poolId, 'selected =', ok);
      await this.delay();
    }

    // Modifier (Straight/Box/PowerBox/… — chip presence depends on the pool).
    // OLD: single click, dropped when the grid re-rendered.
    // const modifierEl = await this.waitForElement(this.wagerDeepLinkService.modifierChipSelector(link.modifier));
    // modifierEl?.click();
    if (link.modifier) {
      await this.clickAndConfirm(
        this.wagerDeepLinkService.modifierChipSelector(link.modifier), 'active-modifier'
      );
      await this.delay();
    }

    // Bet amount — only when it matches a preset chip; otherwise leave the
    // pool's default amount untouched.
    // OLD: single click, dropped when the grid re-rendered.
    // const amountEl = await this.waitForElement(`.bet-amount-chip[data-amount="${link.amount}"]`);
    // amountEl?.click();
    if (link.amount) {
      await this.clickAndConfirm(
        `.bet-amount-chip[data-amount="${link.amount}"]`, 'active'
      );
      await this.delay();
    }

    await this.applyDeepLinkRunners(link);

    // OLD: cleared the fragment, so a refresh lost the shareable link.
    // this.location.replaceState(this.location.path(false));
    // Keep the deep-link in the address bar (restore it if a bootstrap
    // redirect stripped it) so the link stays shareable and a refresh replays.
    this.restoreDeepLinkHash();

    this.cdRef.markForCheck();
  }

  /**
   * Select the deep-link's event + race directly from the loaded list,
   * bypassing the first-landing race. Mirrors `applyPendingSelection`.
   * @param {IWagerDeepLink} link Parsed wager deep-link
   * @returns {void}
   */
  private selectDeepLinkEvent(link: IWagerDeepLink): void {
    const event = _.find(this.events, (e) => e?.id === link.eventId);
    if (!event) return;

    const race = link.raceId
      ? _.find(
          event?.ToteResponse?.EventDetail?.Races,
          (r: IRace) => String(r?.RaceId) === String(link.raceId)
        )
      : undefined;

    this.pendingInitialSelection = undefined;
    this.selectedEvent = event;
    this.currentEventId = event?.id;
    this.prevSelectedEvent = event;

    this.wagerFacade.selectedEventTrack('WagerComponent:deepLink', event);
    this.wagerFacade.selectedRace('WagerComponent:deepLink', {
      ...((race || event?.ToteResponse?.EventDetail?.currentRace) as any),
      runId: event?.ToteResponse?.RunId,
      eventId: event?.ToteResponse?.EventDetail?.EventId,
    });
    this.cdRef.markForCheck();
  }

  /**
   * True when the selected track sells a program today — the PRGM
   * shortcut is hidden otherwise, as in the legacy race info line
   * @returns {boolean} True when the shortcut applies
   */
  hasProgram(): boolean {
    return this.programEventIds.has(String(this.selectedEvent?.id ?? ''));
  }

  /**
   * Open the Program page with the selected track pre-picked. Buying a
   * program charges the account, so guests log in first. Mirrors the
   * legacy `.toProgramshop` hand-off.
   * @returns {void}
   */
  goToProgram(): void {
    if (!this.sessionService.hasToken() || this.sessionService.getUserEmail() === GUEST) {
      this.router.navigate(['/login']);
      return;
    }

    this.router.navigate(['/program'], {
      state: {
        eventId: this.selectedEvent?.id,
        eventName: this.selectedEvent?.ToteResponse?.EventDetail?.EventName,
      }
    });
  }

  /**
   * Collect the tracks selling a program today. Signed-in only — the
   * endpoint needs a token. Mirrors the legacy `listOfexistingeventID`,
   * which the race info line checks before drawing the PRGM link.
   * @returns {void}
   */
  private loadProgramTracks(): void {
    if (!this.sessionService.hasToken() || this.sessionService.getUserEmail() === GUEST) {
      return;
    }

    const today = this.programService.toApiDate(new Date());

    this.wagerFacade.getDataPrograms$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((programs?: IDataPrograms) => {
        this.programsState = programs;

        // The Program page can leave another date in the slice.
        if (programs?.date !== today) return;

        const { tracks } = this.programService.normalizePrograms(programs?.response);

        this.programEventIds = new Set(tracks.map((track) => track.id));
        this.cdRef.markForCheck();
      });

    this.programService.refreshPrograms(
      'WagerComponent:loadProgramTracks', new Date(), this.programsState
    );
  }

  /**
   * If the linked event/race is finished (not wagerable), hand off to the
   * results page with the event/race auto-populated. Anonymous users log in
   * first. Mirrors the manual `selectEvent` finished-race flow.
   * @returns {boolean} True when a redirect was issued (stop replaying)
   */
  private redirectToResultsIfFinished(): boolean {
    const eventDetail = this.selectedEvent?.ToteResponse?.EventDetail;
    const isFinished =
      this.selectedRace?.RaceStatus === 3 // Final
      || eventDetail?.currentRace?.RaceStatus === 3
      || eventDetail?.EventStatus === 'Finished'
      || eventDetail?.EventStatus === 'Final';

    if (!isFinished) return false;

    if (!this.sessionService.hasToken() || this.sessionService.getUserEmail() === GUEST) {
      this.router.navigate(['/login']);
      return true;
    }

    this.router.navigate(['/results'], {
      state: {
        eventId: this.selectedEvent?.id,
        runId: this.selectedEvent?.ToteResponse?.RunId,
        raceId: this.selectedRace?.RaceId ?? eventDetail?.currentRace?.RaceId,
      },
    });
    return true;
  }

  /**
   * Click a chip and confirm it turned active, retrying through the
   * re-renders an event auto-update can trigger (mirrors the legacy retry).
   * @param {string} selector Chip CSS selector
   * @param {string} activeClass Class present once the chip is selected
   * @param {number} tries Max click attempts
   * @returns {Promise<boolean>} True once the chip is active
   */
  private async clickAndConfirm(
    selector: string,
    activeClass: string,
    tries = 6
  ): Promise<boolean> {
    const root = this.elementRef.nativeElement as HTMLElement;

    for (let i = 0; i < tries; i++) {
      const el = await this.waitForElement(selector);
      if (!el) return false;
      if (el.classList.contains(activeClass)) return true;

      el.click();
      const ok = await this.waitForCondition(
        () => root.querySelector(selector)?.classList.contains(activeClass) ?? false,
        1200
      );
      if (ok) return true;
      await this.delay();
    }
    return false;
  }

  /**
   * Restore the shareable deep-link hash on the URL if a bootstrap redirect
   * stripped it, so the address bar shows the full link and a refresh replays.
   * @returns {void}
   */
  private restoreDeepLinkHash(): void {
    const raw = this.wagerDeepLinkService.rawHash;
    if (!raw || window.location.hash === raw) return;

    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search + raw
    );
  }

  /**
   * Apply the encoded per-leg runner selections by clicking each runner's
   * bet cell. Skips guests/anonymous users (a bet click would redirect to
   * login). Mirrors the legacy web `clickRunners`.
   * @param {IWagerDeepLink} link Parsed wager deep-link
   * @returns {Promise<void>}
   */
  private async applyDeepLinkRunners(link: IWagerDeepLink): Promise<void> {
    const legs = link.selectionRunners;
    if (!legs?.length) return;

    // A bet click redirects unidentified users to login — skip for them.
    if (!this.sessionService.hasToken() || this.sessionService.getUserEmail() === GUEST) return;

    const table = await this.waitForElement('.table-runner');
    if (!table) return;

    // Wait for the grid's leg columns to match the encoded legs — multi-race
    // pools (DBL/PK/GSL) render their per-race columns asynchronously.
    await this.waitForCondition(() => this.readLegColumnIds(table).length >= legs.length);
    await this.delay();

    // Leg-column ids ascending — matches how the link encodes legs
    // (single-race legs 1..N, DBL/PK legs = race ids), so index → column id.
    const legIds = this.readLegColumnIds(table);
    const clicksForAbc: Record<string, number> = { A: 1, B: 2, C: 3 };
    const isAbc = link.modifier === 'ABC';

    legs.forEach((leg, legIndex) => {
      const legId = legIds[legIndex];
      if (legId === undefined) return;

      Object.entries(leg ?? {}).forEach(([runnerId, value]) => {
        // Click the bet cell for this runner in this leg/race column.
        const cell = table.querySelector<HTMLElement>(
          `i.runner-${runnerId}.leg-${legId}`
        );
        if (!cell) return;

        const clicks = isAbc ? (clicksForAbc[value] ?? 1) : 1;
        for (let i = 0; i < clicks; i++) cell.click();
      });
    });
  }

  /**
   * Read the distinct leg-column ids (`leg-N` on the bet cells) in
   * ascending order — the column order the shareable link encodes.
   * @param {HTMLElement} table Runner table root
   * @returns {number[]} Leg-column ids ascending
   */
  private readLegColumnIds(table: HTMLElement): number[] {
    const ids = new Set<number>();

    table.querySelectorAll<HTMLElement>('i[class*="leg-"]').forEach((cell) => {
      cell.classList.forEach((cssClass) => {
        const match = /^leg-(\d+)$/.exec(cssClass);
        if (match) ids.add(Number(match[1]));
      });
    });

    return Array.from(ids).sort((a, b) => a - b);
  }

  /**
   * Resolve with the first element matching `selector` inside this
   * component, polling until it appears or the timeout elapses.
   * @param {string} selector CSS selector to look for
   * @param {number} timeoutMs Max wait before resolving null
   * @param {number} intervalMs Poll interval
   * @returns {Promise<HTMLElement | null>} The element, or null on timeout
   */
  private waitForElement(
    selector: string,
    timeoutMs = 8000,
    intervalMs = 60
  ): Promise<HTMLElement | null> {
    const root = this.elementRef.nativeElement as HTMLElement;
    const start = Date.now();

    return new Promise((resolve) => {
      const tick = (): void => {
        const el = root.querySelector<HTMLElement>(selector);
        if (el) return resolve(el);
        if (Date.now() - start >= timeoutMs) return resolve(null);
        setTimeout(tick, intervalMs);
      };
      tick();
    });
  }

  /**
   * Resolve true once `predicate` holds, polling until it does or the
   * timeout elapses.
   * @param {() => boolean} predicate Condition to satisfy
   * @param {number} timeoutMs Max wait before resolving false
   * @param {number} intervalMs Poll interval
   * @returns {Promise<boolean>} True when satisfied, false on timeout
   */
  private waitForCondition(
    predicate: () => boolean,
    timeoutMs = 8000,
    intervalMs = 60
  ): Promise<boolean> {
    const start = Date.now();

    return new Promise((resolve) => {
      const tick = (): void => {
        if (predicate()) return resolve(true);
        if (Date.now() - start >= timeoutMs) return resolve(false);
        setTimeout(tick, intervalMs);
      };
      tick();
    });
  }

  /**
   * Small settle delay so OnPush change detection can flush between the
   * deep-link's chained control clicks.
   * @param {number} ms Milliseconds to wait
   * @returns {Promise<void>}
   */
  private delay(ms = 120): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * scroll to prev or next
   * @param {string} direction user click value prev or next
   * @returns {void}
   */
  scrollTo(direction: 'prev' | 'next'): void {
    const container = this.favorites.nativeElement;
    const scrollAmount = 200;

    if (direction === 'prev') {
      container.scrollLeft -= scrollAmount;
    } else {
      container.scrollLeft += scrollAmount;
    }
  }

}
